import { Link } from 'react-router-dom';
import homeData from '@/data/home.json';
import { useAuth } from '@/lib/auth';
import { useWallet } from '@/lib/data/wallet';
import ServiceCatalogue from '@/components/services/ServiceCatalogue';
import ScrollAmbientBackground from '@/components/common/ScrollAmbientBackground';
import HeroExperienceSimulator from '@/components/home/HeroExperienceSimulator';

export default function Home() {
  const { session, profile } = useAuth();
  const { wallet } = useWallet();
  const isSignedIn = Boolean(session);
  const isCompanion = profile?.role === 'companion' || profile?.role === 'admin';
  const isBrowseUnlocked = Boolean(wallet?.fees?.userFeePaid || profile?.role === 'admin' || profile?.role === 'moderator');

  const { hero, trustBand, howItWorks, trustAndSafety, stories, becomePartner, faq, finalCta } = homeData;

  return (
    <div className="relative">
      {/* ── Dynamic Ambient Background & Scroll Glows ── */}
      <ScrollAmbientBackground />


      {/* ═══ 1. HERO SECTION ═════════════════════════════════════════════════ */}
      <section id="hero" className="relative z-10 w-full max-w-full overflow-hidden py-8 sm:py-14 lg:py-20">
        <div className="mx-auto grid w-full max-w-6xl min-w-0 grid-cols-1 items-center gap-8 px-4 sm:gap-12 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div className="w-full min-w-0 animate-rise">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-line bg-white/80 px-3 py-1 text-xs font-medium text-navy shadow-xs backdrop-blur-md sm:mb-6 sm:px-3.5 sm:py-1.5 sm:text-sm">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="sm:h-4 sm:w-4">
                <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" stroke="#15885A" strokeWidth="2" />
                <path d="M9 12l2 2 4-4" stroke="#15885A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {hero.eyebrow}
            </p>

            <h1 className="w-full break-words text-2xl font-extrabold leading-[1.15] tracking-tight text-navy xs:text-3xl sm:text-4xl sm:leading-[1.08] lg:text-[3.2rem]">
              {hero.headline}
            </h1>

            <p className="mt-4 w-full break-words text-sm leading-relaxed text-slate2 sm:mt-6 sm:text-lg">
              {hero.subheadline}
            </p>

            <div className="mt-6 flex w-full flex-col gap-2.5 sm:mt-9 sm:flex-row sm:gap-3">
              <Link
                to={isSignedIn ? '/companions' : '#pricing'}
                className="w-full rounded-full btn-grad px-6 py-3 text-center text-sm font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5 sm:w-auto sm:px-7 sm:py-3.5 sm:text-base"
              >
                {hero.ctas[0]}
              </Link>
              {isCompanion ? (
                <Link
                  to="/companion"
                  className="w-full rounded-full border border-slate-300/80 bg-white/80 px-6 py-3 text-center text-sm font-semibold text-navy backdrop-blur-md transition-colors hover:border-navy hover:bg-white sm:w-auto sm:px-7 sm:py-3.5 sm:text-base"
                >
                  Companion dashboard
                </Link>
              ) : (
                <a
                  href="#partner"
                  className="w-full rounded-full border border-slate-300/80 bg-white/80 px-6 py-3 text-center text-sm font-semibold text-navy backdrop-blur-md transition-colors hover:border-navy hover:bg-white sm:w-auto sm:px-7 sm:py-3.5 sm:text-base"
                >
                  {hero.ctas[1]}
                </a>
              )}
            </div>

            <ul className="mt-6 flex w-full flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate2 sm:mt-10 sm:gap-x-6 sm:gap-y-2 sm:text-sm">
              {hero.highlights.map((highlight, idx) => (
                <li key={idx} className="flex items-center gap-1.5 sm:gap-2">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-verified" />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Out-Of-The-Box Interactive Experience Simulator */}
          <div className="relative w-full min-w-0 animate-rise" style={{ animationDelay: '150ms' }}>
            <HeroExperienceSimulator />
          </div>
        </div>
      </section>



      {/* ═══ 2. REACH & TRUST BAND SECTION ═══════════════════════════════════ */}
      <section id="trust" className="relative z-10 bg-transparent">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-24">
          <h2 className="brand-ring mx-auto max-w-3xl bg-clip-text text-center text-3xl font-extrabold tracking-tight text-transparent sm:text-4xl">
            {trustBand.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-lg leading-relaxed text-slate2">
            {trustBand.subheading}
          </p>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {trustBand.items.map((item, idx) => {
              const bgClass = idx === 0 ? 'bg-[#FDF0F7]/90 border-pink-200/60' : idx === 1 ? 'bg-[#F3EDFD]/90 border-purple/20' : 'bg-[#EAF3FF]/90 border-blue-200/60';
              const iconBgClass = idx === 0 ? 'bg-[#FBDDEC]' : idx === 1 ? 'bg-[#E6DAFB]' : 'bg-[#D3E6FF]';
              const headingColor = idx === 0 ? 'text-[#9B1560]' : idx === 1 ? 'text-[#4F1A9E]' : 'text-[#163E9C]';
              return (
                <article key={idx} className={`rounded-3xl ${bgClass} border p-8 text-center shadow-lg backdrop-blur-md transition-transform hover:-translate-y-1`}>
                  <div className={`mx-auto grid h-16 w-16 place-items-center rounded-full ${iconBgClass}`}>
                    {idx === 0 && (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E6298A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                    )}
                    {idx === 1 && (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#7B2FE0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="9" cy="8" r="3" />
                        <circle cx="17" cy="9" r="2.5" />
                        <path d="M3 20c.5-3.5 3-5.5 6-5.5s5.5 2 6 5.5M15 14.5c2.5 0 4.5 1.8 5 4.5" />
                      </svg>
                    )}
                    {idx === 2 && (
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2F6BF0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
                        <path d="M9 12l2 2 4-4" />
                      </svg>
                    )}
                  </div>
                  <h3 className={`mt-6 text-xl font-bold ${headingColor}`}>{item.title}</h3>
                  <p className="mt-3 leading-relaxed text-slate2">{item.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>


      {/* ═══ 3. HOW IT WORKS & PRICING SECTION ═══════════════════════════════ */}
      <section id="how" className="relative z-10 mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">

        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--purple)]/30 bg-[var(--purple)]/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[var(--purple)]">
            ⚡ Clear & Simple
          </span>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">{howItWorks.heading}</h2>
          <p className="mt-4 text-lg leading-relaxed text-slate2">{howItWorks.subheading}</p>
        </div>

        <ol className="mt-14 grid gap-8 md:grid-cols-3">
          {howItWorks.steps.map((step) => (
            <li key={step.step} className="relative rounded-3xl border border-white/60 bg-white/70 p-7 shadow-lg backdrop-blur-md transition-transform hover:-translate-y-1 sm:p-8">
              <span className="brand-ring bg-clip-text text-5xl font-extrabold text-transparent">
                {step.step}
              </span>
              <h3 className="mt-4 text-xl font-bold text-navy">{step.title}</h3>
              <p className="mt-2.5 leading-relaxed text-slate2">{step.text}</p>
            </li>
          ))}
        </ol>

        {/* Membership fee card */}
        <div id="pricing" className="mt-16 grid overflow-hidden rounded-3xl border border-brand-line/80 bg-white/80 shadow-xl backdrop-blur-md md:grid-cols-[1fr_auto]">
          <div className="p-8 sm:p-10">
            <span className="inline-block rounded-full bg-[var(--marigold-soft)] px-3 py-1 text-xs font-bold text-[var(--navy)]">
              One-Time Access Pass
            </span>
            <h3 className="mt-2 text-2xl font-bold text-navy">{howItWorks.pricing.title}</h3>
            <p className="mt-3 max-w-xl leading-relaxed text-slate2">{howItWorks.pricing.description}</p>
            <ul className="mt-6 grid gap-2.5 text-sm text-ink sm:grid-cols-2">
              {howItWorks.pricing.includes.map((inc, i) => (
                <li key={i} className="flex gap-2">
                  <svg className="mt-0.5 shrink-0 text-verified" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {inc}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col justify-center gap-4 border-t border-brand-line/80 bg-slate-50/90 p-8 sm:p-10 md:border-l md:border-t-0">
            <p className="text-5xl font-extrabold tracking-tight text-navy">{howItWorks.pricing.price.display}</p>
            <p className="-mt-2 text-sm text-slate2">
              {isBrowseUnlocked ? 'Verification completed' : howItWorks.pricing.price.note}
            </p>
            <Link
              to="/companions"
              className="rounded-full btn-grad px-7 py-3.5 text-center font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5"
            >
              {isBrowseUnlocked
                ? 'Explore directory'
                : isSignedIn
                  ? 'Unlock directory (₹199)'
                  : howItWorks.pricing.cta}
            </Link>
            <p className="text-xs text-slate2 text-center">
              <Link to="/terms" className="underline hover:text-navy">
                {howItWorks.pricing.refundPolicyNote}
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* ═══ 4. SERVICES / EXPERIENCES SECTION (DYNAMIC & LIVE) ══════════════ */}
      <ServiceCatalogue id="services" />

      {/* ═══ 5. TRUST AND SAFETY SECTION ═════════════════════════════════════ */}
      <section id="safety" className="relative z-10 bg-[#070B18]/90 text-white backdrop-blur-md">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--cyan)]/40 bg-[var(--cyan)]/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[var(--cyan)]">
              🛡️ Zero-Tolerance Policy
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">{trustAndSafety.heading}</h2>
            <p className="mt-4 text-lg leading-relaxed text-blue-100/80">{trustAndSafety.subheading}</p>
          </div>

          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {trustAndSafety.pillars.map((pillar, idx) => (
              <article key={idx} className="rounded-3xl border border-white/15 bg-white/[0.06] p-8 shadow-xl backdrop-blur-md">
                {idx === 0 && (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="#7CC6FF" strokeWidth="1.8" />
                    <circle cx="9" cy="11" r="2.2" stroke="#7CC6FF" strokeWidth="1.8" />
                    <path d="M5.8 16c.6-1.6 1.8-2.4 3.2-2.4s2.6.8 3.2 2.4M14.5 10h4M14.5 13.5h3" stroke="#7CC6FF" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                )}
                {idx === 1 && (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="4" y="10" width="16" height="11" rx="2.5" stroke="#7CC6FF" strokeWidth="1.8" />
                    <path d="M8 10V7a4 4 0 118 0v3" stroke="#7CC6FF" strokeWidth="1.8" />
                    <path d="M9 15.5h.01M12 15.5h.01M15 15.5h.01" stroke="#7CC6FF" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                )}
                {idx === 2 && (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" stroke="#7CC6FF" strokeWidth="1.8" />
                    <path d="M9.5 9.5l5 5M14.5 9.5l-5 5" stroke="#7CC6FF" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                )}
                <h3 className="mt-5 text-xl font-bold text-white">{pillar.title}</h3>
                <p className="mt-3 leading-relaxed text-blue-100/80">{pillar.text}</p>
                {pillar.flow && pillar.flow.length >= 2 && (
                  <div className="mt-6 flex items-center gap-3 text-sm">
                    <span className="rounded-full bg-white/10 px-3 py-1 font-semibold">{pillar.flow[0]}</span>
                    <span className="h-px flex-1 bg-white/20" />
                    <span className="rounded-full bg-white/10 px-3 py-1 font-semibold">{pillar.flow[1]}</span>
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="mt-10 rounded-3xl border border-white/15 bg-white/5 p-8 backdrop-blur-md">
            <h3 className="text-lg font-bold text-pink-300">{trustAndSafety.prohibited.title}</h3>
            <p className="mt-3 max-w-3xl leading-relaxed text-blue-100/80">
              {trustAndSafety.prohibited.text}
            </p>
            <Link
              to="/terms"
              className="mt-5 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-bold text-navy transition-colors hover:bg-brand-soft"
            >
              {trustAndSafety.prohibited.cta}
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ 6. STORIES / COMMUNITY TESTIMONIALS SECTION ═════════════════════ */}
      <section id="stories" className="relative z-10 bg-transparent">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">
          <div className="text-center">
            <span className="inline-block rounded-full bg-purple/10 px-3.5 py-1 text-xs font-bold text-purple border border-purple/20">
              {stories.placeholderNote}
            </span>
            <h2 className="brand-ring mx-auto mt-4 max-w-3xl bg-clip-text text-3xl font-extrabold tracking-tight text-transparent sm:text-5xl">
              {stories.heading}
            </h2>
            <p className="mt-4 text-lg text-slate2">{stories.subheading}</p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {stories.testimonials.map((t, idx) => (
              <article
                key={idx}
                className="rounded-3xl border border-white/80 bg-white/80 p-7 shadow-xl backdrop-blur-md transition-transform hover:-translate-y-1 sm:p-9"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="brand-ring grid h-14 w-14 place-items-center rounded-full text-lg font-bold text-white shadow-xs">
                      {t.initials}
                    </span>
                    <div>
                      <p className="text-lg font-bold text-navy">{t.name}</p>
                      <p className="text-sm text-slate2">
                        {t.location}, {t.role} {t.service ? `· ${t.service}` : ''}
                      </p>
                    </div>
                  </div>
                  {t.earningsDisplay ? (
                    <span className="rounded-full bg-[#E7F7EF] px-4 py-1.5 text-base font-bold text-[#0F7A48]">
                      {t.earningsDisplay}
                    </span>
                  ) : (
                    <span className="rounded-full bg-[#F3EDFD] px-3.5 py-1.5 text-sm font-semibold text-purple">
                      {t.service}
                    </span>
                  )}
                </div>

                <blockquote
                  className={`mt-6 border-l-4 pl-5 text-lg italic leading-relaxed text-ink ${
                    idx % 2 === 0 ? 'border-brandpink' : 'border-cyan'
                  }`}
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <p className="mt-5 text-lg text-amber-400" aria-label={`Rated ${t.rating} out of 5 stars`}>
                  {'★'.repeat(t.rating)}
                </p>
              </article>
            ))}
          </div>

          <p className="mx-auto mt-8 max-w-2xl text-center text-xs leading-relaxed text-slate2">
            {stories.disclaimer}
          </p>

          <div className="mt-10 text-center">
            <Link
              to={isCompanion ? '/companion' : '/companion/apply'}
              className="btn-grad inline-block rounded-full px-8 py-4 text-lg font-semibold text-white shadow-md transition-transform hover:-translate-y-0.5"
            >
              {isCompanion ? 'Open companion dashboard' : stories.cta}
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ 7. BECOME A PARTNER SECTION ═════════════════════════════════════ */}
      <section id="partner" className="relative z-10 mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-28">
        <div className="grid items-center gap-10 rounded-3xl border border-white/60 bg-gradient-to-br from-white/90 via-white/80 to-[var(--brand-soft)]/90 p-8 shadow-2xl backdrop-blur-xl sm:p-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--brand)]/30 bg-[var(--brand)]/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[var(--brand)]">
              🤝 Partner With Us
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">{becomePartner.heading}</h2>
            <p className="mt-4 max-w-lg text-lg leading-relaxed text-slate2">{becomePartner.text}</p>
            <Link
              to={isCompanion ? '/companion' : '/companion/apply'}
              className="mt-8 inline-block rounded-full bg-navy px-8 py-4 font-bold text-white shadow-md transition-all hover:bg-ink hover:shadow-lg"
            >
              {isCompanion ? 'Companion dashboard' : becomePartner.cta}
            </Link>
          </div>

          <ul className="space-y-4 text-ink">
            {becomePartner.steps.map((step, idx) => (
              <li key={idx} className="rounded-2xl border border-slate-200/60 bg-white/90 p-5 shadow-xs backdrop-blur-md">
                <p className="font-bold text-navy">{step.title}</p>
                <p className="mt-1 text-sm text-slate2">{step.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ═══ 8. FREQUENTLY ASKED QUESTIONS (FAQ) ══════════════════════════════ */}
      <section id="faq" className="relative z-10 bg-transparent">
        <div className="mx-auto max-w-4xl px-5 py-20 sm:px-8 lg:py-28">
          <h2 className="brand-ring mx-auto bg-clip-text text-center text-3xl font-extrabold tracking-tight text-transparent sm:text-5xl">
            {faq.heading}
          </h2>
          <p className="mt-4 text-center text-lg text-slate2">{faq.subheading}</p>

          <div className="mt-12 space-y-4">
            {faq.items.map((item, idx) => (
              <details
                key={idx}
                className="group rounded-2xl border border-white/80 bg-white/85 shadow-md backdrop-blur-md ring-1 ring-slate-100 open:ring-purple/30"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 text-left text-base font-bold text-navy sm:px-8 sm:text-lg [&::-webkit-details-marker]:hidden">
                  <span>{item.question}</span>
                  <svg
                    className="shrink-0 transition-transform duration-200 group-open:rotate-180"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#7B2FE0"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </summary>
                <p className="px-6 pb-6 leading-relaxed text-slate2 sm:px-8">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 9. WEB APP BAND ═════════════════════════════════════════════════ */}
      <section className="relative z-10 bg-[linear-gradient(120deg,#0A1F63_0%,#2A1670_55%,#6A1F9E_100%)] text-white">
        <div className="mx-auto max-w-6xl px-5 py-20 text-center sm:px-8">
          <p className="text-sm font-bold uppercase tracking-wider text-pink-300">{finalCta.eyebrow}</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-5xl">{finalCta.heading}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-blue-100/80">{finalCta.text}</p>
          <div className="mx-auto mt-9 flex max-w-md flex-col gap-3 sm:flex-row sm:justify-center">
            <Link to="/companions" className="btn-grad rounded-full px-8 py-3.5 font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5">
              {isSignedIn ? 'Explore companions' : finalCta.ctas[0]}
            </Link>
            <Link
              to={isCompanion ? '/companion' : '/companion/apply'}
              className="rounded-full bg-white/10 px-8 py-3.5 font-semibold text-white ring-1 ring-white/25 backdrop-blur-md transition-colors hover:bg-white/20"
            >
              {isCompanion ? 'Companion dashboard' : finalCta.ctas[1]}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


